import { ref } from 'vue'
import axios from 'axios'
import { getMimeTypeFromExtension, normalizeExtension } from '@/utils/file-utils'

export interface DocumentRow {
  fldGUID?: string
  fldID?: number | string
  fldDokNr?: number | string
  fldNr?: number | string
  fldExt?: string
  fldNa?: string
  id?: number | string
}

export interface DocumentResult {
  fldExt?: string
  fldNa?: string
  fnGetContentsAsBase64?: string
  [key: string]: unknown
}

export function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes
}

/**
 * Decodes a base64-encoded string into its UTF-8 text representation.
 * Used for previewing text-based documents (e.g. XML) without downloading them.
 *
 * @param base64 The base64-encoded content
 * @returns The decoded text content
 */
export function base64ToText(base64: string): string {
  return new TextDecoder('utf-8').decode(base64ToUint8Array(base64))
}

export function createBlobUrlFromBase64(base64: string, extensionOrMime?: string): string {
  const mimeType = extensionOrMime?.includes('/')
    ? extensionOrMime
    : getMimeTypeFromExtension(normalizeExtension(extensionOrMime || ''))
  const byteArray = base64ToUint8Array(base64)
  const blob = new Blob([byteArray.buffer as ArrayBuffer], { type: mimeType })
  return URL.createObjectURL(blob)
}

export async function fetchDocumentContent(row: DocumentRow): Promise<DocumentResult | null> {
  const guid = row.fldGUID
  const rawId = row.fldID ?? row.fldDokNr ?? row.fldNr ?? row.id
  const id = rawId !== undefined && rawId !== null && rawId !== '' ? rawId : null

  // 1. Try GUID lookup if GUID is available
  if (guid) {
    const queryByGuid = `
      query GetDocByGUID {
        tblDocuments {
          rowRead(exactMatch: { byGUID: { kf1GUID: { guid: "${guid}" } } }) {
            fldExt
            fldNa
            fnGetContentsAsBase64
          }
        }
      }
    `
    try {
      const response = await axios.post('/api/microtech/v1/proxy/graphql', {
        query: queryByGuid,
        variables: {},
      })
      const doc = response.data?.data?.tblDocuments?.rowRead
      if (doc && doc.fnGetContentsAsBase64) {
        return doc
      }
    } catch (e) {
      console.warn('Failed to fetch document by GUID, trying fallback if ID available:', e)
    }
  }

  // 2. Try ID lookup as fallback or primary if GUID was missing/failed
  if (id !== null) {
    const queryById = `
      query GetDocByID {
        tblDocuments {
          rowRead(kf1ID: { int: ${id} }) {
            fldExt
            fldNa
            fnGetContentsAsBase64
          }
        }
      }
    `
    try {
      const response = await axios.post('/api/microtech/v1/proxy/graphql', {
        query: queryById,
        variables: {},
      })
      const doc = response.data?.data?.tblDocuments?.rowRead
      if (doc && doc.fnGetContentsAsBase64) {
        return doc
      }
    } catch (e) {
      console.error('Failed to fetch document by ID:', e)
    }
  }

  return null
}

export function useDocumentDownload() {
  const downloading = ref(false)
  const loadingDocs = ref<Record<string, boolean>>({})

  const showToastError = (message: string) => {
    try {
      if (typeof useToast === 'function') {
        const toast = useToast()
        if (toast && typeof toast.add === 'function') {
          toast.add({
            title: 'Dokumentenfehler',
            description: message,
            color: 'error',
          })
        }
      }
    } catch {
      // Ignore if outside Nuxt app context
    }
  }

  const downloadDocument = async (row: DocumentRow): Promise<boolean> => {
    const guid = row.fldGUID
    if (guid) {
      loadingDocs.value[guid] = true
    }
    downloading.value = true

    try {
      const doc = await fetchDocumentContent(row)
      if (!doc || !doc.fnGetContentsAsBase64) {
        showToastError('Dokument konnte nicht abgerufen werden oder ist leer.')
        return false
      }

      const base64 = doc.fnGetContentsAsBase64
      const extension = normalizeExtension(doc.fldExt || (row.fldExt as string) || '')
      const mimeType = getMimeTypeFromExtension(extension)
      const fileName = doc.fldNa || (row.fldNa as string) || 'document'
      const fullName = fileName.endsWith(extension) ? fileName : `${fileName}${extension}`

      const url = createBlobUrlFromBase64(base64, mimeType)
      const link = document.createElement('a')
      link.href = url
      link.download = fullName
      document.body.appendChild(link)
      link.click()

      setTimeout(() => {
        if (link.parentNode) {
          link.parentNode.removeChild(link)
        }
        URL.revokeObjectURL(url)
      }, 100)

      return true
    } catch (error) {
      console.error('Document download failed:', error)
      showToastError('Fehler beim Herunterladen des Dokuments.')
      return false
    } finally {
      downloading.value = false
      if (guid) {
        delete loadingDocs.value[guid]
      }
    }
  }

  return {
    downloading,
    loadingDocs,
    downloadDocument,
    fetchDocumentContent,
    createBlobUrlFromBase64,
    base64ToUint8Array,
  }
}
